import React, { useMemo } from "react";
import {
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  ChevronRight,
  Clock3,
  Minus,
  Sparkles,
  XCircle,
} from "lucide-react";
import { buildTimelineSections, formatAgo } from "../../utils/activityHelpers";
import {
  formatBalance,
  getTxDateKey,
  summarizeTxLedger,
  txAmountParts,
  txCurrencyLabel,
  txStatusLabel,
  txTypeLabel,
} from "../../utils/dashboardHelpers";

const FLOW_ICON = {
  in: ArrowDownLeft,
  out: ArrowUpRight,
  pending: Clock3,
  failed: XCircle,
  neutral: Minus,
};

const txFlow = (tone, status = "") => {
  const s = String(status).toLowerCase();
  if (["pending", "hold", "onhold"].includes(s)) return { key: "pending", label: "Pending" };
  if (["failed", "cancelled", "canceled"].includes(s)) return { key: "failed", label: "Failed" };
  if (tone === "credit") return { key: "in", label: "In" };
  if (tone === "debit") return { key: "out", label: "Out" };
  return { key: "neutral", label: "Neutral" };
};

const ActivityStreamItem = ({ tx, onClick, isLast }) => {
  const { prefix, amount, tone } = txAmountParts(tx);
  const flow = txFlow(tone, tx.status);
  const FlowIcon = FLOW_ICON[flow.key] || Minus;
  const status = String(tx.status || "").toLowerCase();
  const showStatus = ["pending", "hold", "onhold", "failed", "cancelled", "canceled"].includes(status);
  const currency = txCurrencyLabel(tx.currency);

  return (
    <li className={`ra-stream-item ra-stream-${flow.key}`}>
      <button type="button" className="ra-stream-btn" onClick={onClick}>
        <span className="ra-stream-rail" aria-hidden>
          <span className="ra-stream-dot" />
          {!isLast ? <span className="ra-stream-line" /> : null}
        </span>

        <span className={`ra-stream-flow ra-stream-flow-${flow.key}`} title={flow.label}>
          <FlowIcon size={12} />
        </span>

        <span className="ra-stream-body">
          <span className="ra-stream-top">
            <strong>{txTypeLabel(tx.transactionType)}</strong>
            {showStatus ? (
              <em className={`ra-stream-flag ra-stream-flag-${flow.key}`}>
                {txStatusLabel(tx.status)}
              </em>
            ) : null}
          </span>
          <span className="ra-stream-meta">
            {formatAgo(tx.createdAt)}
            {currency ? ` · ${currency}` : ""}
          </span>
        </span>

        <span className="ra-stream-amt tabular-nums">
          {prefix}
          {formatBalance(amount, { compact: amount >= 10000 })}
        </span>

        <ChevronRight size={13} className="ra-stream-chevron" aria-hidden />
      </button>
    </li>
  );
};

const DashboardManageGrid = ({
  transactions = [],
  transactionsLoading = false,
  onViewActivity,
}) => {
  const recent = useMemo(() => transactions.slice(0, 10), [transactions]);
  const sections = useMemo(
    () => buildTimelineSections(recent, getTxDateKey),
    [recent]
  );
  const summary = useMemo(() => summarizeTxLedger(recent), [recent]);
  const net = summary.credits - summary.debits;

  return (
    <section className="ws-root" aria-label="Workspace">
      <div className="ws-activity">
        <header className="ws-activity-head">
          <div className="ws-activity-title">
            <span className="ws-activity-kicker">
              <Activity size={11} aria-hidden />
              Activity feed
            </span>
            <h3>Recent</h3>
          </div>

          {recent.length > 0 && (
            <div className="ws-activity-actions">
              <div className="ws-activity-pulse tabular-nums" aria-label="Recent sample summary">
                <span className="ws-activity-pulse-in">
                  In {formatBalance(summary.credits, { compact: true })}
                </span>
                <span className="ws-activity-pulse-out">
                  Out {formatBalance(summary.debits, { compact: true })}
                </span>
                <span className={net >= 0 ? "ws-activity-pulse-net-up" : "ws-activity-pulse-net-down"}>
                  Net {net >= 0 ? "+" : ""}
                  {formatBalance(net, { compact: true })}
                </span>
              </div>
              <button type="button" className="ws-activity-open" onClick={onViewActivity}>
                Open ledger
                <ChevronRight size={13} />
              </button>
            </div>
          )}
        </header>

        {transactionsLoading && !transactions.length ? (
          <div className="ra-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className="ra-skel-row" />
            ))}
          </div>
        ) : recent.length > 0 ? (
          <div className="ra-stream-groups">
            {sections.map((section) => (
              <section key={section.dateKey} className="ra-stream-day">
                <div className="ra-stream-day-head">
                  <span>{section.title}</span>
                  <em>{section.data.length}</em>
                </div>
                <ul className="ra-stream-list">
                  {section.data.map((tx, idx) => (
                    <ActivityStreamItem
                      key={tx._id || tx.transactionRef}
                      tx={tx}
                      onClick={onViewActivity}
                      isLast={idx === section.data.length - 1}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <div className="ra-empty">
            <span className="ra-empty-icon">
              <Sparkles size={22} />
            </span>
            <p>No activities yet</p>
            <span className="ra-empty-hint">Deposits, bets, and wins will show up here.</span>
          </div>
        )}

        {recent.length > 0 && transactions.length > recent.length ? (
          <footer className="ws-activity-foot">
            <button type="button" className="ws-activity-more" onClick={onViewActivity}>
              View all {transactions.length} in ledger
              <ChevronRight size={13} />
            </button>
          </footer>
        ) : null}
      </div>
    </section>
  );
};

export default DashboardManageGrid;
